<template>
  <WebLayout
    title="Sobre RealGestión"
    description="Conoce más sobre RealGestión, nuestra misión y el equipo que hace posible la gestión eficiente de tu negocio."
    url="https://realgestion.lat/about"
    class="bg-white font-sans selection:bg-orange-500/30"
  >
    <Head :title="`Sobre RealGestión - Conoce más sobre nosotros`" />

    <!-- Hero Section -->
    <div class="relative overflow-hidden bg-gray-50 min-h-[60vh] flex items-center pt-20 pb-16 border-b border-gray-100">
      <!-- Decoraciones de fondo -->
      <div class="absolute top-0 left-1/2 w-full -translate-x-1/2 h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-orange-400/10 blur-[120px]" />
        <div class="absolute top-[20%] -right-[10%] w-[40%] h-[60%] rounded-full bg-rose-400/10 blur-[120px]" />
        <div class="absolute -bottom-[20%] left-[20%] w-[60%] h-[50%] rounded-full bg-indigo-400/10 blur-[120px]" />
        <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wMykiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]" />
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center max-w-4xl mx-auto">
          <div class="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-600 text-sm font-medium mb-6">
            Nuestra Misión
          </div>
          <h1 class="text-5xl tracking-tight font-extrabold sm:text-6xl md:text-7xl lg:leading-[1.1]">
            <span class="block text-slate-900">Impulsando el futuro de</span>
            <span class="block bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-rose-500 to-orange-600 pb-2">los negocios en Venezuela</span>
          </h1>
          <p class="mt-6 mx-auto text-xl text-slate-600 font-light leading-relaxed max-w-3xl">
            En Venezuela existen más de 2 millones de microempresas, de las cuales la gran mayoría aún no ha adoptado tecnología digital. ¡Estamos hablando de miles de negocios y emprendedores que necesitan herramientas para crecer!
          </p>
          <p class="mt-4 mx-auto text-lg text-slate-500 font-light leading-relaxed max-w-2xl">
            Es por ello que en RealGestión buscamos brindarles las herramientas tecnológicas necesarias para ayudarlos a controlar sus finanzas, tomar mejores decisiones y hacer crecer su negocio en un entorno cada vez más digital. <br /><br /> Creemos en el potencial de los emprendedores venezolanos. Estamos aquí para ser sus aliados en este camino de crecimiento.
          </p>
        </div>
      </div>
    </div>

    <!-- Why RealGestión Section -->
    <div class="bg-white py-24 relative z-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:text-center max-w-3xl mx-auto mb-16">
          <h2 class="text-base text-orange-600 font-bold tracking-wide uppercase flex items-center justify-center">
            <span class="w-8 h-px bg-orange-500/50 mr-3" />
            ¿Por qué RealGestión?
            <span class="w-8 h-px bg-orange-500/50 ml-3" />
          </h2>
          <p class="mt-4 text-4xl leading-tight font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Democratizando la <span class="text-slate-400">digitalización</span>
          </p>
          <p class="mt-6 text-xl text-slate-600 font-light">
            financiera de los pequeños comerciantes y emprendedores venezolanos.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="group relative bg-white border border-gray-200 hover:border-orange-500/30 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.15)] overflow-hidden">
            <div class="absolute -inset-px bg-gradient-to-b from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl" />
            <div class="relative z-10">
              <div class="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange-50 text-orange-500 border border-orange-100 group-hover:bg-orange-500 group-hover:text-white group-hover:border-orange-400 transition-colors duration-300 mb-6 shadow-sm">
                <i class="fas fa-users text-2xl" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Inclusión</h3>
              <p class="text-slate-600 leading-relaxed font-light">
                Buscamos impulsar a los pequeños negocios venezolanos por medio de la inclusión digital y financiera.
              </p>
            </div>
          </div>
          
          <div class="group relative bg-white border border-gray-200 hover:border-orange-500/30 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.15)] overflow-hidden">
            <div class="absolute -inset-px bg-gradient-to-b from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl" />
            <div class="relative z-10">
              <div class="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange-50 text-orange-500 border border-orange-100 group-hover:bg-orange-500 group-hover:text-white group-hover:border-orange-400 transition-colors duration-300 mb-6 shadow-sm">
                <i class="fas fa-lightbulb text-2xl" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Innovación</h3>
              <p class="text-slate-600 leading-relaxed font-light">
                Soñamos con ser la plataforma líder en Venezuela para microempresarios y emprendedores.
              </p>
            </div>
          </div>
          
          <div class="group relative bg-white border border-gray-200 hover:border-orange-500/30 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.15)] overflow-hidden">
            <div class="absolute -inset-px bg-gradient-to-b from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl" />
            <div class="relative z-10">
              <div class="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange-50 text-orange-500 border border-orange-100 group-hover:bg-orange-500 group-hover:text-white group-hover:border-orange-400 transition-colors duration-300 mb-6 shadow-sm">
                <i class="fas fa-rocket text-2xl" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Impacto</h3>
              <p class="text-slate-600 leading-relaxed font-light">
                Nuestro objetivo es transformar la forma en que los pequeños negocios operan en Venezuela.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Section -->
    <div class="bg-gray-50 py-24 border-t border-gray-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <h2 class="text-base text-orange-600 font-bold tracking-wide uppercase flex items-center justify-center">
            <span class="w-8 h-px bg-orange-500/50 mr-3" />
            El equipo
            <span class="w-8 h-px bg-orange-500/50 ml-3" />
          </h2>
          <h3 class="mt-4 text-4xl leading-tight font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Que hace todo <span class="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-rose-500">posible</span>
          </h3>
          <p class="mt-6 text-xl text-slate-600 font-light">
            RealGestión nació de la iniciativa de un equipo apasionado por la tecnología y el emprendimiento en Venezuela, comprometidos con apoyar a los negocios locales.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Team Member 1 -->
          <div class="bg-white rounded-3xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.15)] transition-all duration-500 relative group flex flex-col overflow-hidden">
            <div class="absolute -inset-px bg-gradient-to-b from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <div class="p-8 relative z-10 flex-grow">
              <h3 class="text-2xl font-bold text-slate-900 mb-1">David Arteaga</h3>
              <p class="text-orange-500 font-medium text-sm">Fundador & CEO</p>
              <p class="mt-4 text-slate-600 leading-relaxed font-light">
                Apasionado por la tecnología y el emprendimiento, con más de 10 años de experiencia en desarrollo de software y gestión de proyectos.
              </p>
            </div>
            <div class="p-6 pt-0 border-t border-gray-50 flex items-center justify-end relative z-10 space-x-4">
              <a href="https://instagram.com/yosoydavidarteaga" target="_blank" rel="noopener noreferrer" class="text-slate-400 hover:text-orange-500 transition-colors">
                <span class="sr-only">Instagram</span>
                <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
                </svg>
              </a>
              <a href="https://linkedin.com/in/soydavidarteaga" target="_blank" rel="noopener noreferrer" class="text-slate-400 hover:text-orange-500 transition-colors">
                <span class="sr-only">LinkedIn</span>
                <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clip-rule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          <!-- Team Member 2 -->
          <div class="bg-white rounded-3xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.15)] transition-all duration-500 relative group flex flex-col overflow-hidden">
            <div class="absolute -inset-px bg-gradient-to-b from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <div class="p-8 relative z-10 flex-grow">
              <h3 class="text-2xl font-bold text-slate-900 mb-1">Agustin Gastel</h3>
              <p class="text-orange-500 font-medium text-sm">Gerente de Mercadeo</p>
              <p class="mt-4 text-slate-600 leading-relaxed font-light">
                Mente creativa enfocada en conectar los valores de nuestra marca con las necesidades reales de los usuarios, impulsando el alcance y la identidad digital de RealGestión.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Values Section -->
    <div class="bg-white py-24 border-t border-gray-100 relative z-10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <h2 class="text-base text-orange-600 font-bold tracking-wide uppercase flex items-center justify-center">
            <span class="w-8 h-px bg-orange-500/50 mr-3" />
            Cultura
            <span class="w-8 h-px bg-orange-500/50 ml-3" />
          </h2>
          <h3 class="mt-4 text-4xl leading-tight font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Nuestro <span class="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-rose-500">ADN</span>
          </h3>
        </div>

        <div class="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-heart text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Pasión</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              Por apoyar a los pequeños negocios venezolanos con las mejores herramientas.
            </p>
          </div>

          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-user-check text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Primero el usuario</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              Lo que necesita el usuario es nuestra prioridad en la toma de decisiones.
            </p>
          </div>

          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-globe text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Piensa en grande</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              Queremos ser el aliado que lleve la digitalización a todos los micronegocios de Venezuela.
            </p>
          </div>

          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-sitemap text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Estructura Plana</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              Las buenas ideas pueden venir de cualquiera, desde los fundadores hasta un practicante.
            </p>
          </div>

          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-chart-line text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Meritocracia</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              El desempeño excepcional siempre tendrá su recompensa en nuestro equipo.
            </p>
          </div>

          <div class="group bg-gray-50 border border-gray-100 hover:border-orange-200 rounded-3xl p-8 transition-all duration-300">
            <div class="mb-4 inline-flex items-center text-orange-500 bg-orange-100/50 p-3 rounded-xl border border-orange-100">
              <i class="fas fa-people-carry text-xl" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">Pertenencia</h3>
            <p class="text-slate-600 font-light leading-relaxed">
              Todos y cada uno debemos pensar y actuar como dueños del producto.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="relative py-24 overflow-hidden border-t border-gray-100">
      <!-- Fondo degradado -->
      <div class="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 pointer-events-none" />
      <!-- Patrón de puntos -->
      <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] pointer-events-none opacity-60" />

      <div class="relative z-10 max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
        <h2 class="text-4xl font-extrabold text-white tracking-tight sm:text-5xl mb-4">
          ¿Quieres ser parte de nuestro equipo?
        </h2>
        <p class="text-lg leading-relaxed text-orange-100 font-light mb-8 pt-4">
          Únete a nosotros en nuestra misión de transformar la forma en que los pequeños negocios operan en Venezuela.
        </p>
        <a href="mailto:hola@realgestion.lat" class="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white text-orange-600 font-bold text-base shadow-[0_4px_20px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 transition-all duration-300">
          <i class="fas fa-envelope mr-2" /> Contáctanos
        </a>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout,
  },
}
</script>